import { useUser } from '@clerk/nextjs';
import { resolveCrossSellBookingLinkModel } from '@web-app/components/CallV2/SessionPrepTab/resolveCrossSellBookingLinkModel';
import { trpc } from '@web-app/utils/trpc';

const STALE_TIME_MS = 60 * 1000;

type UseCrossSellBookingLinkInput = {
  sourceAppointmentHealthieId?: string | null;
  patientHealthieId?: string | null;
};

export function useCrossSellBookingLink({
  sourceAppointmentHealthieId,
  patientHealthieId,
}: UseCrossSellBookingLinkInput) {
  const { user } = useUser();
  const utils = trpc.useUtils();
  const sourceAppointmentId = sourceAppointmentHealthieId ?? '';
  const patientId = patientHealthieId ?? '';
  const hasRequiredIds = Boolean(sourceAppointmentId && patientId);

  const offerQuery = trpc.sessionPrep.getCrossSellBookingOffer.useQuery(
    {
      sourceAppointmentHealthieId: sourceAppointmentId,
      patientHealthieId: patientId,
      // Prevent a same-tab provider switch from reusing the previous viewer's result.
      viewerId: user?.id ?? '',
    },
    {
      enabled: hasRequiredIds,
      staleTime: STALE_TIME_MS,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const prepareMutation =
    trpc.sessionPrep.prepareCrossSellBookingLink.useMutation();
  const sendMutation = trpc.sessionPrep.sendCrossSellBookingLink.useMutation({
    onSuccess: () => utils.sessionPrep.getCrossSellBookingOffer.invalidate(),
  });

  const model = resolveCrossSellBookingLinkModel({
    hasRequiredIds,
    isLoading: offerQuery.isPending,
    isError: offerQuery.isError,
    targetProgram: offerQuery.data?.targetProgram,
    chatDeliveryCompleted: offerQuery.data?.chatDeliveryCompleted,
    smsDeliveryCompleted: offerQuery.data?.smsDeliveryCompleted,
    sentAt: offerQuery.data?.sentAt,
  });

  async function prepareLink(): Promise<string> {
    const link = await prepareMutation.mutateAsync({
      sourceAppointmentHealthieId: sourceAppointmentId,
      patientHealthieId: patientId,
    });
    return link.startUrl;
  }

  async function sendLink() {
    return sendMutation.mutateAsync({
      sourceAppointmentHealthieId: sourceAppointmentId,
      patientHealthieId: patientId,
    });
  }

  return {
    model,
    prepareLink,
    sendLink,
    isPreparing: prepareMutation.isPending,
    isSending: sendMutation.isPending,
  };
}
